import InfoIcon from '@mui/icons-material/Info';
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationTooltip } from './InformationTooltip';
import { getConfidenceRangeDisplay, getProbabilityColor } from './utils';

export const PredictionColoringTooltip = memo(function (): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const ranges = getConfidenceRangeDisplay();

  function ColorStrip(props: { color: string; text: string; range: string }) {
    const { color, text, range } = props;
    return (
      <Stack direction='row' spacing={2} alignItems={'center'}>
        <Box
          sx={{
            borderRadius: '6px',
            height: '1.5rem',
            width: '2rem',
            flexShrink: 0,
            backgroundColor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          <Box flex={1} />
          <Box
            sx={{
              width: '6px',
              backgroundColor: color,
              borderRadius: '0px 4px 4px 0px',
            }}
          />
        </Box>
        <Stack direction='column' spacing={0}>
          <Typography color='black'>{text}</Typography>
          <Typography color='gray' variant='caption'>
            {range}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <InformationTooltip
      tooltipElement={InfoIcon}
      fontSize={isMobile ? 'small' : 'medium'}
      tooltipContent={
        <Stack direction='column' spacing={2}>
          <Typography variant='body1'>{t('prediction.confidence.tooltipTitle')}</Typography>
          <Divider />
          <ColorStrip
            color={getProbabilityColor('high')}
            text={t('prediction.confidence.highLevel')}
            range={ranges.high}
          />
          <ColorStrip
            color={getProbabilityColor('medium')}
            text={t('prediction.confidence.mediumLevel')}
            range={ranges.medium}
          />
          <ColorStrip
            color={getProbabilityColor('low')}
            text={t('prediction.confidence.lowLevel')}
            range={ranges.low}
          />
        </Stack>
      }
    />
  );
});
